import { useEffect, useId, useMemo, useRef, useState } from "react";
import {
  tsParticles,
  type Container,
  type Engine,
  type ISourceOptions,
} from "@tsparticles/engine";
import { loadSlim } from "@tsparticles/slim";

const PARTICLE_COUNT = 70;
const TABLET_PARTICLE_COUNT = 36;
const MOBILE_PARTICLE_COUNT = 22;
const PARTICLE_SPEED = 0.35;
const LINK_DISTANCE = 130;
const PARTICLE_COLOR = "#67e8f9";
const LINK_COLOR = "#38bdf8";
const LINK_OPACITY = 1;
const PARTICLE_OPACITY = { min: 0.7, max: 0.46 };
const PARTICLE_SIZE = { min: 3, max: 6 };

let particlesEnginePromise: Promise<void> | null = null;

const initializeParticles = (engine: Engine) => {
  particlesEnginePromise ??= loadSlim(engine).then(() => undefined);

  return particlesEnginePromise;
};

const usePrefersReducedMotion = () => {
  const getInitialPreference = () => {
    if (typeof window === "undefined") {
      return false;
    }

    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  };

  const [prefersReducedMotion, setPrefersReducedMotion] =
    useState(getInitialPreference);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const handleChange = () => setPrefersReducedMotion(mediaQuery.matches);

    handleChange();
    mediaQuery.addEventListener("change", handleChange);

    return () => {
      mediaQuery.removeEventListener("change", handleChange);
    };
  }, []);

  return prefersReducedMotion;
};

const StaticNetwork = () => (
  <svg
    className="animated-network-static"
    viewBox="0 0 900 700"
    preserveAspectRatio="xMidYMid slice"
    focusable="false"
  >
    <g
      fill="none"
      stroke={LINK_COLOR}
      strokeLinecap="round"
      strokeWidth="1.4"
      opacity="0.42"
    >
      <path d="M82 182 L220 112 L352 198 L520 146 L688 232" />
      <path d="M220 112 L286 336 L442 286 L520 146" />
      <path d="M92 420 L286 336 L438 514 L640 428 L760 560" />
      <path d="M352 198 L442 286 L640 428" />
      <path d="M286 336 L230 572 L438 514" />
    </g>
    <g fill={PARTICLE_COLOR} opacity="0.82">
      <circle cx="82" cy="182" r="4.5" />
      <circle cx="220" cy="112" r="5.5" />
      <circle cx="352" cy="198" r="4.2" />
      <circle cx="520" cy="146" r="5" />
      <circle cx="688" cy="232" r="4" />
      <circle cx="286" cy="336" r="5.5" />
      <circle cx="442" cy="286" r="4.5" />
      <circle cx="92" cy="420" r="4" />
      <circle cx="438" cy="514" r="5" />
      <circle cx="640" cy="428" r="4.5" />
      <circle cx="230" cy="572" r="4" />
      <circle cx="760" cy="560" r="5" />
    </g>
  </svg>
);

const NetworkParticles = ({ options }: { options: ISourceOptions }) => {
  const particlesId = useId().replace(/:/g, "-");
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    let container: Container | undefined;

    const mountParticles = async () => {
      const element = containerRef.current;

      if (!element) {
        return;
      }

      await initializeParticles(tsParticles);

      if (cancelled || !element.isConnected) {
        return;
      }

      container = await tsParticles.load({
        id: `network-background-${particlesId}`,
        element,
        options,
      });

      if (cancelled || !element.isConnected) {
        container?.destroy();
      }
    };

    void mountParticles();

    return () => {
      cancelled = true;
      container?.destroy();
      containerRef.current?.querySelectorAll("canvas").forEach((canvas) => {
        canvas.remove();
      });
    };
  }, [options, particlesId]);

  return <div ref={containerRef} className="h-full w-full" />;
};

export const AnimatedNetworkBackground = () => {
  const prefersReducedMotion = usePrefersReducedMotion();

  const particlesOptions = useMemo<ISourceOptions>(
    () => ({
      autoPlay: true,
      background: {
        color: {
          value: "transparent",
        },
      },
      detectRetina: true,
      fpsLimit: 40,
      fullScreen: {
        enable: false,
      },
      interactivity: {
        events: {
          resize: {
            enable: true,
          },
        },
      },
      particles: {
        color: {
          value: PARTICLE_COLOR,
        },
        links: {
          blink: false,
          color: LINK_COLOR,
          consent: false,
          distance: LINK_DISTANCE,
          enable: true,
          opacity: LINK_OPACITY,
          width: 1,
        },
        move: {
          direction: "none",
          enable: true,
          outModes: {
            default: "bounce",
          },
          random: true,
          speed: PARTICLE_SPEED,
          straight: false,
        },
        number: {
          value: PARTICLE_COUNT,
        },
        opacity: {
          value: PARTICLE_OPACITY,
          animation: {
            enable: true,
            speed: 0.16,
            sync: false,
          },
        },
        shape: {
          type: "circle",
        },
        size: {
          value: PARTICLE_SIZE,
          animation: {
            enable: true,
            speed: 0.28,
            sync: false,
          },
        },
      },
      responsive: [
        {
          maxWidth: 1024,
          options: {
            particles: {
              links: {
                distance: 155,
                opacity: 0.14,
              },
              number: {
                value: TABLET_PARTICLE_COUNT,
              },
            },
          },
        },
        {
          maxWidth: 640,
          options: {
            fpsLimit: 32,
            particles: {
              links: {
                distance: 120,
                opacity: 0.11,
              },
              move: {
                speed: 0.2,
              },
              number: {
                value: MOBILE_PARTICLE_COUNT,
              },
              opacity: {
                value: {
                  min: 0.2,
                  max: 0.38,
                },
              },
              size: {
                value: {
                  min: 1.5,
                  max: 3,
                },
              },
            },
          },
        },
      ],
    }),
    [],
  );

  return (
    <div className="animated-network-background" aria-hidden="true">
      <StaticNetwork />
      {!prefersReducedMotion && <NetworkParticles options={particlesOptions} />}
    </div>
  );
};
